import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, CardMedia, IconButton, Modal } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useGetItemsQuery, useDeleteItemMutation } from '../store/docApi';
import QuestForm from './QuestForm';

function Quests() {
  const navigate = useNavigate();
  const { data: quests, isLoading, error } = useGetItemsQuery({ label: 'quest' });
  const [deleteItem] = useDeleteItemMutation();
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDelete = async (id) => {
    try {
      await deleteItem({ label: 'quest', id }).unwrap();
    } catch (error) {
      console.error('Failed to delete quest:', error);
    }
  };

  const handleEdit = (quest) => {
    setSelectedQuest(quest);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedQuest(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuest(null);
  };

  const handleQuestClick = (quest) => {
    navigate(`/quests/${quest.id}`);
  };

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error loading quests</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Quests
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" color="primary" onClick={handleAdd}>
          Add Quest
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'center'
        }}
      >
        {quests?.map((quest) => (
          <Card
            key={quest.id}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              width: 300,
              transition: 'box-shadow 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 1
              }
            }}
            onClick={() => handleQuestClick(quest)}
          >
            {quest.picture && (
              <CardMedia
                component="img"
                image={quest.picture}
                alt={quest.name}
                sx={{
                  height: 140,
                  objectFit: 'cover'
                }}
              />
            )}
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {quest.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {quest.description}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1
                }}
                onClick={(e) => e.stopPropagation()} // Prevent card click when clicking buttons
              >
                <IconButton
                  size="small"
                  onClick={() => handleEdit(quest)}
                  sx={{
                    color: 'text.primary',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(quest.id)}
                  sx={{
                    color: 'text.primary',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: '90%',
            maxWidth: 500
          }}
        >
          <QuestForm quest={selectedQuest} onClose={handleCloseModal} />
        </Box>
      </Modal>
    </Box>
  );
}

export default Quests;
